import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";

import { RequireAuth } from "./auth/RequireAuth";
import { AppLayout } from "./layout/AppLayout";
import { PublicLayout } from "./layout/PublicLayout";
import { CollectionOverviewEdit } from "./collections/CollectionOverviewEdit";
import { CollectionRedirect, CollectionScreen } from "./screens/CollectionScreen";
import {
  DocumentHistoryScreen,
  DocumentScreen,
  DocumentShortRedirect,
  NewDocumentScreen,
} from "./screens/DocumentScreens";
import { HomeRedirect, HomeScreen } from "./screens/HomeScreen";
import { ArchiveScreen, DraftsScreen, TrashScreen } from "./screens/ListScreens";
import { LoginScreen } from "./screens/LoginScreen";
import { SearchScreen } from "./screens/SearchScreen";
import { LogoutScreen, NotFoundScreen } from "./screens/StatusScreens";
import { ApiKeysScreen } from "./screens/settings/ApiKeysScreen";
import {
  ApiAndAccessScreen,
  FeaturesSettingsScreen,
  TemplateScreen,
  TemplatesScreen,
} from "./screens/settings/PlaceholderScreens";
import { SharesScreen } from "./screens/settings/SharesScreen";
import { ExportScreen } from "./screens/settings/ExportScreen";
import { ImportScreen } from "./screens/settings/ImportScreen";
import { NotificationSettingsScreen } from "./screens/settings/NotificationSettingsScreen";
import { PreferencesScreen } from "./screens/settings/PreferencesScreen";
import { AuthenticationSettingsScreen } from "./screens/settings/AuthenticationSettingsScreen";
import { SecuritySettingsScreen } from "./screens/settings/SecuritySettingsScreen";
import { WorkspaceDetailsScreen } from "./screens/settings/WorkspaceDetailsScreen";
import { GroupMembersScreen } from "./screens/settings/GroupMembersScreen";
import { GroupsScreen } from "./screens/settings/GroupsScreen";
import { MembersScreen } from "./screens/settings/MembersScreen";
import { ProfileScreen } from "./screens/settings/ProfileScreen";
import { SettingsLayout } from "./screens/settings/SettingsLayout";
import { ShareDocumentScreen, ShareScreen } from "./screens/share/ShareScreens";

/**
 * The editor harness is development-only and carries the editor and KaTeX with
 * it, so it is a lazy route: production builds put it in a chunk nothing ever
 * asks for.
 */
const DebugEditorScreen = lazy(() =>
  import("./screens/DebugEditorScreen").then((module) => ({ default: module.DebugEditorScreen })),
);

/** The collaboration harness, for driving a live session from two browsers by hand. */
const DebugCollabScreen = lazy(() =>
  import("./screens/DebugCollabScreen").then((module) => ({ default: module.DebugCollabScreen })),
);

/**
 * `/collection/:slug/new` creates a document in this collection; the creation
 * context is the same one `/doc/new` takes, so it simply forwards.
 */
function NewCollectionDocument(): ReactNode {
  const { slug } = useParams();
  return <Navigate to={`/doc/new?collectionId=${encodeURIComponent(slug ?? "")}`} replace />;
}

/** `/collections/*` is the legacy spelling of `/collection/*` (note 03 §3.2). */
function LegacyCollectionRedirect(): ReactNode {
  const location = useLocation();
  return <Navigate to={location.pathname.replace("/collections/", "/collection/")} replace />;
}

/** `/share/:shareId` is the legacy spelling of `/s/:shareId`. */
function LegacyShareRedirect(): ReactNode {
  const { shareId } = useParams();
  return <Navigate to={`/s/${shareId ?? ""}`} replace />;
}

/** The legacy spelling of a document inside a share (`docs/spec/shares.md` §3.4). */
function LegacyShareDocumentRedirect(): ReactNode {
  const { shareId, slug } = useParams();
  return <Navigate to={`/s/${shareId ?? ""}/doc/${slug ?? ""}`} replace />;
}

/**
 * The route table. It mirrors the URL scheme in note 03 §3 — those paths are a
 * compatibility surface (links and bookmarks travel between installations), so
 * the spellings match exactly even where our screens are still placeholders.
 */
export function AppRoutes(): ReactNode {
  return (
    <Routes>
      {/* Always available */}
      <Route path="/" element={<LoginScreen />} />
      <Route path="/create" element={<LoginScreen mode="create" />} />
      <Route path="/logout" element={<LogoutScreen />} />

      {/* Published shares: no session required */}
      <Route element={<PublicLayout />}>
        <Route path="/s/:shareId" element={<ShareScreen />} />
        <Route path="/s/:shareId/doc/:slug" element={<ShareDocumentScreen />} />
        <Route path="/s/:shareId/*" element={<ShareScreen />} />
      </Route>
      <Route path="/share/:shareId" element={<LegacyShareRedirect />} />
      <Route path="/share/:shareId/doc/:slug" element={<LegacyShareDocumentRedirect />} />

      {/* The authenticated application */}
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/home" element={<HomeRedirect />} />
        <Route path="/home/:tab" element={<HomeScreen />} />
        <Route path="/dashboard" element={<Navigate to="/home" replace />} />
        <Route path="/starred" element={<Navigate to="/home" replace />} />

        <Route path="/drafts" element={<DraftsScreen />} />
        <Route path="/archive" element={<ArchiveScreen />} />
        <Route path="/trash" element={<TrashScreen />} />

        <Route path="/collection/:slug" element={<CollectionRedirect />} />
        <Route path="/collection/:slug/overview/edit" element={<CollectionOverviewEdit />} />
        <Route path="/collection/:slug/new" element={<NewCollectionDocument />} />
        <Route path="/collection/:slug/:tab" element={<CollectionScreen />} />
        <Route path="/collections/*" element={<LegacyCollectionRedirect />} />

        <Route path="/doc/new" element={<NewDocumentScreen />} />
        <Route path="/doc/:slug" element={<DocumentScreen />} />
        <Route path="/doc/:slug/edit" element={<DocumentScreen mode="edit" />} />
        <Route path="/doc/:slug/history" element={<DocumentHistoryScreen />} />
        <Route path="/doc/:slug/history/:revisionId" element={<DocumentHistoryScreen />} />
        <Route path="/d/:slug" element={<DocumentShortRedirect />} />

        <Route path="/search" element={<SearchScreen />} />
        <Route path="/search/:query" element={<SearchScreen />} />

        <Route path="/settings" element={<SettingsLayout />}>
          <Route index element={<ProfileScreen />} />
          <Route path="preferences" element={<PreferencesScreen />} />
          <Route path="notifications" element={<NotificationSettingsScreen />} />
          <Route path="api-and-access" element={<ApiAndAccessScreen />} />
          <Route path="details" element={<WorkspaceDetailsScreen />} />
          <Route path="authentication" element={<AuthenticationSettingsScreen />} />
          <Route path="security" element={<SecuritySettingsScreen />} />
          <Route path="features" element={<FeaturesSettingsScreen />} />
          <Route path="users" element={<MembersScreen />} />
          <Route path="members" element={<Navigate to="/settings/users" replace />} />
          <Route path="groups" element={<GroupsScreen />} />
          <Route path="groups/:id/members" element={<GroupMembersScreen />} />
          <Route path="templates" element={<TemplatesScreen />} />
          <Route path="templates/new" element={<TemplateScreen />} />
          <Route path="templates/:id" element={<TemplateScreen />} />
          <Route path="api-keys" element={<ApiKeysScreen />} />
          <Route path="shares" element={<SharesScreen />} />
          <Route path="import" element={<ImportScreen />} />
          <Route path="export" element={<ExportScreen />} />
        </Route>
        <Route path="/templates" element={<Navigate to="/settings/templates" replace />} />
      </Route>

      {/* A development-only harness for the editor engine; never in a build. */}
      {import.meta.env.DEV && (
        <Route
          path="/debug/editor"
          element={
            <Suspense fallback={null}>
              <DebugEditorScreen />
            </Suspense>
          }
        />
      )}
      {import.meta.env.DEV && (
        <Route
          path="/debug/collab"
          element={
            <Suspense fallback={null}>
              <DebugCollabScreen />
            </Suspense>
          }
        />
      )}

      <Route path="/404" element={<NotFoundScreen />} />
      <Route path="*" element={<NotFoundScreen />} />
    </Routes>
  );
}
